import { useEffect, useRef, useState } from 'react';
import { CheckIcon, CopyIcon } from '@newcore/icons';
import { Sizes } from '../../enums/sizes';
import { ButtonVariants } from '../../enums/variants';
import { cn } from '../../utils/cn';
import { Tooltip } from '../tooltips';
import { Button, type ButtonSize } from './button';

interface CopyButtonProps {
  value: string;
  className?: string;
  size?: ButtonSize;
  variant?: Exclude<ButtonVariants, ButtonVariants.ICON>;
  title?: string;
  copiedTitle?: string;
  copiedDuration?: number;
}

export function CopyButton({
  value,
  className,
  size = Sizes.SM,
  variant = ButtonVariants.TERTIARY,
  title = 'Copy to clipboard',
  copiedTitle = 'Copied!',
  copiedDuration = 2000,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => setCopied(false), copiedDuration);
    } catch {
      // Clipboard API unavailable or permission denied
    }
  };

  return (
    <Tooltip content={copied ? copiedTitle : title}>
      <Button
        onClick={handleCopy}
        variant={variant}
        size={size}
        className={cn(
          'px-2 gap-0 text-tertiary hover:text-primary transition-colors',
          className
        )}
        type='button'
        aria-label={title}
        data-testid='copy-button'
      >
        {copied ? (
          <CheckIcon className='text-utility-green-600' />
        ) : (
          <CopyIcon />
        )}
      </Button>
    </Tooltip>
  );
}
